import { useQuery } from "@tanstack/react-query";
import type { FunctionComponent } from "react";
import { useMemo } from "react";

import { useAggregatedDataV2 } from "~/hooks/useAggregatedDataV2.ts";
import userViewGuard from "~/hooks/useViewPermssionsCheck";
import { invoiceStatusQueries } from "~/modules/billing/api/invoiceStatus/invoiceStatusQueries.ts";
import { useAllProcessedInvoices } from "~/modules/billing/api/processedInvoice/processedInvoiceQueries.ts";
import type { InvoicesIndexTableData } from "~/modules/billing/components/InvoicesIndexView/InvoicesIndexTableTypes.ts";
import InvoicesIndexView from "~/modules/billing/components/InvoicesIndexView/InvoicesIndexView.tsx";
import { PermissionNames } from "~/types/entityNames.ts";

const InvoicesIndexPage: FunctionComponent = () => {
	const guard = userViewGuard([PermissionNames.CanManageInvoices, PermissionNames.CanViewInvoices]);

	if (guard) return guard;

	const { data, options } = useAggregatedDataV2({
		invoices: useAllProcessedInvoices(),
		statuses:useQuery(invoiceStatusQueries.all())
	});
	const { invoices, statuses } = data;

	const tableData: InvoicesIndexTableData[] = useMemo(() => {
		if (invoices && statuses) {
			const statusIdToOrderMap = new Map(statuses.map((status, index) => [status.id, index]));
			return invoices
				.map((invoice) => {
					const transactionDate = invoice.sentAt || invoice.receivedAt;
					const isOverdue = invoice.paymentDueAt && invoice.paidAt === null ? new Date() > new Date(invoice.paymentDueAt) : false;
					return {
						...invoice,
						deliveryEndDate: new Date(invoice.deliveryEndDate),
						deliveryStartDate: new Date(invoice.deliveryStartDate),
						invoiceStatusOrder: statusIdToOrderMap.get(invoice.invoiceStatusId) ?? Number.MAX_SAFE_INTEGER,
						isOverdue,
						paidAt: invoice.paidAt ? new Date(invoice.paidAt) : null,
						paymentDueAt: invoice.paymentDueAt ? new Date(invoice.paymentDueAt) : null,
						sentAt: invoice.sentAt ? new Date(invoice.sentAt) : null,
						transactionDate: transactionDate ? new Date(transactionDate) : null,
						updatedAt: new Date(invoice.updatedAt),
					};
				});

		}
		return [];
	}, [invoices, statuses]);

	return <InvoicesIndexView isLoading={options.isLoading}
							  invoiceStatuses={statuses || []}
							  tableData={tableData} />;
};

export default InvoicesIndexPage;